/**
 * Detection Rules v2 — landing page.
 *
 * Per-type metric cards source their numbers directly from the per-type list
 * endpoints (3 hooks each: total / published / drafts) rather than the
 * cross-type query — authoritative + cheaper. The per-type lists live for
 * all five types (Wave 2 complete).
 *
 * Feature flag `rulesV2` gates the page; when false an "under maintenance"
 * splash covers the 30-minute backend/frontend flip window per design §8.3.
 */

import { Link } from "@tanstack/react-router";
import { Download, GitPullRequestArrow, Settings2 } from "lucide-react";

import { featureFlags } from "~/lib/featureFlags";
import {
    RULE_TYPES,
    type RuleType,
    useReviewQueue,
} from "~/lib/rules";
import { useYaraList, type YaraListFilters } from "~/lib/rules-yara";
import { useSigmaList, type SigmaListFilters } from "~/lib/rules-sigma";
import { useSuricataList, type SuricataListFilters } from "~/lib/rules-suricata";
import { useElasticRules, type ElasticListFilters } from "~/lib/rules-elastic";
import { useCustomRules, type CustomListFilters } from "~/lib/rules-custom";

export function RulesPage(): React.ReactElement {
    if (!featureFlags.rulesV2) {
        return <MaintenanceSplash />;
    }

    return (
        <section className="stack" style={{ padding: "var(--space-6)", gap: "var(--space-6)" }}>
            <header className="stack-xs">
                <h1 className="type-h1">Detection rules</h1>
                <p className="type-body-muted">
                    Five first-class types. Cross-type reviews, imports and governance live here; per-type
                    authoring lands on the type's own page.
                </p>
            </header>

            <div
                className="grid"
                style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "var(--space-4)" }}
            >
                {RULE_TYPES.map((t) => (
                    <TypeMetricCard key={t.value} type={t.value} label={t.label} chipClass={t.chipClass} />
                ))}
            </div>

            <div className="card-surface" style={{ padding: "var(--space-5)" }}>
                <h2 className="type-h3" style={{ marginBottom: "var(--space-3)" }}>Common actions</h2>
                <div className="cluster" style={{ gap: "var(--space-3)", flexWrap: "wrap" }}>
                    <Link to="/rules/reviews" className="btn btn--ghost">
                        <GitPullRequestArrow size={14} />
                        <span>Review queue</span>
                        <PendingBadge />
                    </Link>
                    <Link to="/rules/settings" className="btn btn--ghost">
                        <Settings2 size={14} />
                        <span>Governance & registries</span>
                    </Link>
                    <button type="button" className="btn btn--ghost" disabled title="Per-type imports live in each type's page">
                        <Download size={14} />
                        <span>Imports (per type)</span>
                    </button>
                </div>
            </div>
        </section>
    );
}

// Per-type counts: each card uses the type's own list endpoint with limit:1
// for the totals (cheap — server returns total separately from rows).
// Three hooks per card: overall, status=published, status=draft.

function useYaraCounts(): { total: number | string; published: number; drafts: number } {
    const all = useYaraList({ limit: 1 } satisfies YaraListFilters);
    const pub = useYaraList({ limit: 1, status: "published" } satisfies YaraListFilters);
    const drf = useYaraList({ limit: 1, status: "draft" } satisfies YaraListFilters);
    return {
        total: all.data?.total ?? "—",
        published: pub.data?.total ?? 0,
        drafts: drf.data?.total ?? 0,
    };
}
function useSigmaCounts(): { total: number | string; published: number; drafts: number } {
    const all = useSigmaList({ limit: 1 } satisfies SigmaListFilters);
    const pub = useSigmaList({ limit: 1, status: "published" } satisfies SigmaListFilters);
    const drf = useSigmaList({ limit: 1, status: "draft" } satisfies SigmaListFilters);
    return {
        total: all.data?.total ?? "—",
        published: pub.data?.total ?? 0,
        drafts: drf.data?.total ?? 0,
    };
}
function useSuricataCounts(): { total: number | string; published: number; drafts: number } {
    const all = useSuricataList({ limit: 1 } satisfies SuricataListFilters);
    const pub = useSuricataList({ limit: 1, status: "published" } satisfies SuricataListFilters);
    const drf = useSuricataList({ limit: 1, status: "draft" } satisfies SuricataListFilters);
    return {
        total: all.data?.total ?? "—",
        published: pub.data?.total ?? 0,
        drafts: drf.data?.total ?? 0,
    };
}
function useElasticCounts(): { total: number | string; published: number; drafts: number } {
    const all = useElasticRules({ limit: 1 } satisfies ElasticListFilters);
    const pub = useElasticRules({ limit: 1, status: "published" } satisfies ElasticListFilters);
    const drf = useElasticRules({ limit: 1, status: "draft" } satisfies ElasticListFilters);
    return {
        total: all.data?.total ?? "—",
        published: pub.data?.total ?? 0,
        drafts: drf.data?.total ?? 0,
    };
}
function useCustomCounts(): { total: number | string; published: number; drafts: number } {
    const all = useCustomRules({ limit: 1 } satisfies CustomListFilters);
    const pub = useCustomRules({ limit: 1, status: "published" } satisfies CustomListFilters);
    const drf = useCustomRules({ limit: 1, status: "draft" } satisfies CustomListFilters);
    return {
        total: all.data?.total ?? "—",
        published: pub.data?.total ?? 0,
        drafts: drf.data?.total ?? 0,
    };
}

function TypeMetricCard({
    type, label, chipClass,
}: { type: RuleType; label: string; chipClass: string }): React.ReactElement {
    // Always invoke all 5 hooks — react-query keys make extra fetches a no-op
    // for non-matching types? No — hooks are unconditional, so we fan out and
    // pick the one for this card. Parallel fetches are fine (15 calls total
    // across 5 cards is trivial relative to other dashboard fetches).
    const yara = useYaraCounts();
    const sigma = useSigmaCounts();
    const suricata = useSuricataCounts();
    const elastic = useElasticCounts();
    const custom = useCustomCounts();

    const counts =
        type === "yara" ? yara :
        type === "sigma" ? sigma :
        type === "suricata" ? suricata :
        type === "elastic" ? elastic :
        type === "custom" ? custom :
        { total: "—" as const, published: 0, drafts: 0 };

    const to = `/rules/${type}`;

    const card = (
        <div className="metric-card" style={{ padding: "var(--space-4)", position: "relative" }}>
            <div className="cluster-xs" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                <span className={`chip ${chipClass}`}>{label}</span>
                <span className="type-mono-sm text-text-muted">{counts.total}</span>
            </div>
            <p
                className="text-12 text-text-muted"
                style={{ marginTop: "var(--space-3)" }}
            >
                {counts.published} published · {counts.drafts} drafts
            </p>
        </div>
    );
    return <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>{card}</Link>;
}

function PendingBadge(): React.ReactElement | null {
    const q = useReviewQueue({ status: "pending" });
    const n = q.data?.length ?? 0;
    if (n === 0) return null;
    return <span className="chip chip--warn">{n}</span>;
}

function MaintenanceSplash(): React.ReactElement {
    return (
        <section className="stack" style={{ padding: "var(--space-6)", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
            <div className="card-surface" style={{ padding: "var(--space-6)", maxWidth: 520, textAlign: "center" }}>
                <h1 className="type-h2">Rules subsystem is being upgraded</h1>
                <p className="type-body-muted" style={{ marginTop: "var(--space-3)" }}>
                    The detection-rules subsystem is mid-flip to v2. Reviews and authoring resume shortly.
                </p>
            </div>
        </section>
    );
}
